/**
 * The parts a record's own page is written in, below the frame
 * (DetailPageFrame): the question each tab answers, its section headings, and
 * the in-place editors a row or a rail line opens.
 *
 * Moved here from the item page (features/item-detail-third-pass/
 * ItemDetailThirdPassView.tsx), which re-exports them, so a commitment's page
 * and an item's page are written in the same parts. Markup and behaviour
 * unchanged.
 */
import { useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Callout, TextArea, TextInput } from '@/components/ds';
import { btnPrimary, btnSecondary } from '@/components/mono/primitives';
import { ChoiceList, type ChoiceOption } from '@/components/shared/ChoiceList';
import { useEditorSave } from './useEditorSave';

const RULE = { borderColor: 'var(--rule)' } as const;

/** The question a tab answers, at its top. */
export function DestinationQuestion({ children }: { children: ReactNode }) {
  return (
    <p className="font-serif text-[19px]" style={{ color: 'var(--text-pri)' }}>
      {children}
    </p>
  );
}

/** Section heading with an optional ownership note ("owned by this item · edit here"). */
export function OwnedSectionHeading({ title, owned, children, id }: { title: ReactNode; owned?: string; children?: ReactNode; id?: string }) {
  return (
    <div id={id} className="mt-6 flex scroll-mt-24 flex-wrap items-baseline justify-between gap-3 border-b pb-2" style={RULE}>
      <h3 className="text-[16.5px] font-semibold" style={{ color: 'var(--text-pri)' }}>
        {title}
      </h3>
      {owned && <span className="font-mono text-[11px] uppercase tracking-[0.07em] text-[var(--text-dim)]">{owned}</span>}
      {children && <div className="flex flex-wrap gap-2">{children}</div>}
    </div>
  );
}

// ── Inline editors (used by the real page's row editors) ─────────────────────

export function EditorButtons({ onSave, onCancel, saving, saveLabel = 'Save' }: { onSave: () => void; onCancel: () => void; saving: boolean; saveLabel?: string }) {
  return (
    <div className="mt-2 flex gap-2">
      <button {...btnPrimary()} onClick={onSave} disabled={saving}>
        {saving ? 'Saving…' : saveLabel}
      </button>
      <button {...btnSecondary()} onClick={onCancel} disabled={saving}>
        Cancel
      </button>
    </div>
  );
}

export function EditorError({ error }: { error: string | null }) {
  if (!error) return null;
  return (
    <Callout tone="coral" label="Not saved" className="mt-2">
      {error}
    </Callout>
  );
}

export function InlineTextEditor({
  initial,
  multiline,
  placeholder,
  label,
  onSave,
  close,
  inputType,
}: {
  initial: string;
  multiline?: boolean;
  placeholder?: string;
  label: string;
  onSave: (value: string) => Promise<string | null | void>;
  close: () => void;
  inputType?: 'text' | 'number' | 'date';
}) {
  const [value, setValue] = useState(initial);
  const { saving, error, run } = useEditorSave(() => onSave(value.trim()), close);
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
    } else if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      void run();
    }
  };
  return (
    <div>
      {multiline ? (
        <TextArea aria-label={label} rows={3} value={value} placeholder={placeholder} autoFocus onKeyDown={onKeyDown} onChange={(e) => setValue(e.target.value)} />
      ) : (
        <TextInput aria-label={label} type={inputType} value={value} placeholder={placeholder} autoFocus onKeyDown={onKeyDown} onChange={(e) => setValue(e.target.value)} />
      )}
      <EditorError error={error} />
      <EditorButtons onSave={() => void run()} onCancel={close} saving={saving} />
    </div>
  );
}

/** Pick one option; picking saves. Clear removes the value. */
export function InlineChoiceEditor({
  initial,
  options,
  label,
  onSave,
  close,
  allowClear = true,
  stacked,
}: {
  initial: string;
  options: ChoiceOption[];
  label: string;
  onSave: (value: string) => Promise<string | null | void>;
  close: () => void;
  allowClear?: boolean;
  stacked?: boolean;
}) {
  const [picked, setPicked] = useState(initial);
  const pickedRef = useRef(initial);
  const { saving, error, run } = useEditorSave(() => onSave(pickedRef.current), close);
  const pick = (v: string) => {
    pickedRef.current = v;
    setPicked(v);
    void run();
  };
  return (
    <div
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          close();
        }
      }}
    >
      <ChoiceList options={options} value={picked} onPick={pick} label={label} disabled={saving} stacked={stacked} />
      <EditorError error={error} />
      <div className="mt-2 flex gap-2">
        {allowClear && initial && (
          <button {...btnSecondary()} onClick={() => pick('')} disabled={saving}>
            Clear
          </button>
        )}
        <button {...btnSecondary()} onClick={close} disabled={saving}>
          {saving ? 'Saving…' : 'Cancel'}
        </button>
      </div>
    </div>
  );
}
